import { useAuth } from '../contexts/AuthContext';
import { hasFullAccess } from '../lib/permissions';

/**
 * Check if the current user has a specific permission.
 * Full-access roles (super_admin, system) always return true.
 */
export function usePermission(permission) {
  const { user } = useAuth();
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  return user.effective_permissions?.includes(permission) ?? false;
}

/**
 * Check if the current user has ANY of the given permissions.
 */
export function useAnyPermission(...permissions) {
  const { user } = useAuth();
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  const ep = user.effective_permissions ?? [];
  return permissions.some((p) => ep.includes(p));
}

/**
 * Check if the current user has ALL of the given permissions.
 */
export function useAllPermissions(...permissions) {
  const { user } = useAuth();
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  const ep = user.effective_permissions ?? [];
  return permissions.every((p) => ep.includes(p));
}

/**
 * Non-hook helper for permission checks (use inside callbacks/effects).
 */
export function hasPermission(user, permission) {
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  return user.effective_permissions?.includes(permission) ?? false;
}
